<script setup lang="ts">
import { ref } from 'vue'

// Define props for the component
defineProps<{
  side: 'left' | 'right'
  displayName: string
  alignment: string
  bio: string
  assistantId: string
  corpusTitles: string[]
  avatarImage: string
  isMobile: boolean
}>()

// Define emits for events
const emit = defineEmits<{
  (e: 'toggle-corpus-modal'): void
  (e: 'view-philosopher', philosopher: string, alignment: string, method: string): void
}>()

// State for bio visibility
const showBio = ref(false)

// Function to toggle bio visibility
function toggleBio(isMobile: boolean, displayName: string, alignment: string) {
  if (isMobile) {
    showBio.value = !showBio.value
    
    // If bio is now visible, emit the view event
    if (showBio.value) {
      const method = isMobile ? 'click' : 'hover'
      emit('view-philosopher', displayName, alignment, method)
    }
  }
}

// Handlers for mouse enter/leave (desktop view)
function handleMouseEnter(isMobile: boolean, displayName: string, alignment: string) {
  if (!isMobile) {
    showBio.value = true
    const method = 'hover'
    emit('view-philosopher', displayName, alignment, method)
  }
}

function handleMouseLeave(isMobile: boolean) {
  if (!isMobile) {
    showBio.value = false
  }
}
</script>

<template>
  <div class="philosopher-header-container">
    <div 
      class="avatar-container"
      @mouseenter="handleMouseEnter(isMobile, displayName, alignment)"
      @mouseleave="handleMouseLeave(isMobile)"
      @click="toggleBio(isMobile, displayName, alignment)"
    >
      <div class="avatar-wrapper">
        <img
          :src="avatarImage"
          :alt="`${side} perspective`"
          class="perspective-image"
        />
      </div>
      <h2>{{ displayName }}</h2>
      <div class="philosopher-alignment">{{ alignment }}</div>
      
      <!-- Bio popup -->
      <div v-if="showBio" class="bio-popup" :class="`${side}-bio`">
        <!-- For mobile view -->
        <template v-if="isMobile">
          <div class="mobile-bio-header">About {{ displayName }}</div>
          <div class="mobile-bio-text">{{ bio }}</div>
          <span class="close-button" @click.stop="showBio = false">×</span>
        </template>

        <!-- For desktop view -->
        <div v-else class="bio-content" :data-name="displayName">
          <span class="bio-text">{{ bio }}</span>
        </div>
      </div>

      <!-- Mobile overlay -->
      <div
        v-if="isMobile && showBio"
        class="mobile-overlay"
        @click="showBio = false"
      ></div>
    </div>
    
    <!-- Corpus button (now outside the avatar container, but inside the overall component) -->
    <button 
      class="corpus-list-button" 
      :class="`${side}-corpus-button`" 
      @click.stop="emit('toggle-corpus-modal')"
      title="View corpus titles"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
      </svg>
    </button>
  </div>
</template>

<style scoped>
/* Container for the entire component */
.philosopher-header-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Corpus button positioning */
.corpus-list-button {
  position: absolute;
  width: 22px;
  height: 22px;
  bottom: 5px; /* Position at the bottom of the header */
  z-index: 10;
}

.left-corpus-button {
  left: 10px; /* Position at bottom left of the header */
}

.right-corpus-button {
  right: 10px; /* Position at bottom right of the header */
}

/* Add spacing above the avatar and ensure enough width */
.avatar-container {
  min-width: 100px; /* Ensure enough horizontal space */
  padding-top: 12px; /* Add spacing above the avatar */
}
</style>